import { sortAsc } from '@strata/helper-functions';
import { FormContainer } from '@strata/react-form-container';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import { Fragment, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { AuditHistory } from 'components/AuditHistory';
import { FormPage } from 'components/FormPage';
import { ListPageHeader } from 'components/ListPageHeader';
import { RoutePreviewSection } from 'pages/routeManagement/RoutePreviewSection';
import { RouteRuleForm } from 'pages/routeManagement/RouteRuleForm';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { mapPayerSegment } from 'services/PayerSegmentSupport';
import {
    getSegmentPlacement,
    globalRouteAppRoute,
    routeRulesUrl,
    useConditionOptions,
    useConfiguredSegmentRoutes,
} from 'services/RouteRuleHelpers';

export const RouteRuleDetails = () => {
    const { segmentId, benefit, routeRuleId } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(routeRulesUrl(segmentId, benefit));

    const isCreate = routeRuleId === CREATE_ROUTE;

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegment = payerSegments?.find((segment) => segment.segment_id === segmentId);
    const payerId = payerSegment?.payer_id;
    const payerSegmentOptions = useMemo(
        () => payerSegments?.map(mapPayerSegment).sort(({ name: a }, { name: b }) => sortAsc(a, b)),
        [payerSegments],
    );

    const { conditionOptionsMap, networkLists, supplierOptions } = useConditionOptions(
        segmentId,
        benefit,
    );

    const routeRule = useGet(
        !isCreate && payerId
            ? `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}/${routeRuleId}`
            : null,
    );

    // The rule is only configured in the segment it is being edited in
    const configuredSegments =
        isCreate || routeRule
            ? [
                  {
                      payer_id: payerId,
                      segment_id: segmentId,
                      // The single rule endpoint does not return the id the url already carries
                      ...(!isCreate && { route_rule_id: routeRuleId }),
                      ...routeRule,
                  },
              ]
            : undefined;

    const routesBySegment = useConfiguredSegmentRoutes(configuredSegments, benefit);

    const auditHistoryData = useGet(
        !isCreate && payerId
            ? `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}/${routeRuleId}/audit`
            : null,
    );

    const isLoading = !(isCreate || routeRule);

    // Global routes are configured for every payer segment at once, on their own page
    if (routeRule?.is_global) {
        return <Navigate to={globalRouteAppRoute(routeRuleId)} replace />;
    }

    return (
        <Formik
            initialValues={RouteRuleForm.initialValues(
                routeRule,
                configuredSegments,
                routesBySegment,
                segmentId,
            )}
            enableReinitialize
            validationSchema={Yup.object(RouteRuleForm.validationSchema)}
            onSubmit={async (values) => {
                try {
                    const [segment] = values.payer_segments;
                    const body = {
                        ...RouteRuleForm.submitValues(values),
                        ...getSegmentPlacement(segment),
                    };
                    const url = `/clients/admin/${segment.payer_id}/${segment.segment_id}/route_rules/${benefit}`;

                    await (segment.existing
                        ? put(`${url}/${segment.existing.route_rule_id}`, body)
                        : post(url, body));

                    await SuccessToast({ message: 'Route Rule saved successfully.' });

                    goHome();
                } catch (err) {
                    handleError('Error saving route rule.', err);
                }
            }}
        >
            {(formik) => (
                <FormPage
                    columns={[
                        <FormContainer
                            key="form"
                            formTitle="Edit Route Rule"
                            isLoading={isLoading}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                            onSecondaryButtonClick={goHome}
                            formLeftContent={
                                <>
                                    <ListPageHeader pageTitle="Route Rule" />
                                    <RouteRuleForm
                                        benefit={benefit}
                                        conditionOptionsMap={conditionOptionsMap}
                                        payerSegmentOptions={payerSegmentOptions}
                                        configuredSegmentRoutes={routesBySegment}
                                        currentSegmentId={segmentId}
                                    />
                                </>
                            }
                        />,
                        <Fragment key="preview">
                            <RoutePreviewSection
                                benefit={benefit}
                                isLoading={isLoading}
                                payerSegmentOptions={payerSegmentOptions}
                                conditionOptionsMap={conditionOptionsMap}
                                supplierOptions={supplierOptions}
                                networkOptions={networkLists}
                            />
                            <AuditHistory data={auditHistoryData} isCreate={isCreate} />
                        </Fragment>,
                    ]}
                />
            )}
        </Formik>
    );
};
